<template>
  <div class="mini-editor">
    <div class="rounded-lg mini-editor-menu-bar">
      <div class="menubar text-sm flex justify-center items-center text-gray-400 rounded-t-lg bg-white">
        <!-- merge fields -->
        <merge-field-dropdown :options="emailMergeFieldDropdownOptions" :command="insertMergeField" mode="mini" />
      </div>
    </div>
    <!-- /menubar -->

    <div class="el-textarea el-input--4">
      <div class="el-textarea__inner border-none">
        <editor-content class="mt-1 text-brand-black font-medium text-sm leading-6" :editor="editor" />
        <span class="el-input__count" v-if="maxlength > 0">{{ length }}/{{ maxlength }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'; // required for this.$prompt
import { mapGetters } from 'vuex';
import { Editor, EditorContent } from '@tiptap/vue-2';
import MergeField from '@/components/UI/Editor/MergeField/MergeFieldExtension';
import MergeFieldDropdown from '@/components/UI/Editor/MergeField/MergeFieldDropdown.vue';
import OneLiner from '@/components/UI/Editor/OneLiner';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';

export default {
  name: 'Editor',
  props: {
    placeholder: String,
    maxlength: {
      type: Number,
      default: 0,
    },
  },
  components: {
    EditorContent,
    MergeFieldDropdown,
  },
  data() {
    return {
      editor: new Editor({
        disableInputRules: true,
        extensions: [
          OneLiner,
          Text,
          Paragraph,
          MergeField,
        ],
        content: this.placeholder,
        editorProps: {
          handleTextInput: (view) => {
            if (this.maxlength && view.state.doc.textContent.length >= this.maxlength && view.state.selection.empty) {
              return true;
            }

            return false;
          },
          handlePaste: (view, event, slice) => {
            if (this.maxlength && view.state.doc.textContent.length + slice.size > this.maxlength) {
              slice.content = new DocumentFragment();
            }
          },
        },
      }),
      length: 0,
    };
  },
  computed: {
    ...mapGetters(['mergeFields']),
    emailMergeFieldDropdownOptions() {
      return [
        {
          label: 'Email Merge Fields',
          items: this.mergeFields,
        },
      ];
    },
  },
  created() {
    // Set the length based on the textContent from ProseMirror
    this.length = this.editor.state.doc.textContent.length;

    // Emit generated HTML back up to the parent component
    this.editor.on('update', ({ editor }) => {
      this.relayContents();
      this.length = editor.getText().length;
    });

    // Emit on load
    this.relayContents(false);
  },
  methods: {
    relayContents(includeUpdate = true) {
      if (includeUpdate) {
        this.$emit('update', this.editor.getHTML());
      }
      this.$emit('jsonUpdate', this.editor.getJSON());
    },
    focusEditor() {
      this.$nextTick(() => {
        this.editor.chain().focus().run();
      });
    },
    insertMergeField({ id, label, fallback }) {
      this.editor.commands.insertMergeField({ id, label, fallback });
    },
  },

  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss">
.mini-editor {
  position: relative;
}

.mini-editor-menu-bar {
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 100%;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mini-editor-menu-bar .is-active {
  color: black;
}
.mini-editor-menu-bar button {
  color: #3c5ccf;
  border: none;
  background: none;
  cursor: pointer;
  outline: none;
}

.mini-editor .el-textarea__inner {
  padding-top: 0;
}

.mini-editor .el-textarea .el-input__count {
  bottom: -25px;
  @apply text-gray-500;
}

/* Overrides for ProseMirror styles because TipTap doesn't give us a way to add classes inside the editor */
.mini-editor .ProseMirror {
  min-height: 1em;
  outline: none !important;
  margin-right: 40px;
  @apply text-brand-blue;
}
</style>
